import { Input, Pagination } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';

import { useCatalogue, useItemCategories } from '@/api/masters';
import { ApiErrorAlert } from '@/components/ApiErrorAlert';
import { DatePicker } from '@/components/DatePicker';
import { Card, EmptyState, PageHeading } from '@/components/PageHeading';
import { useDebounced } from '@/components/useDebounced';
import { chargeableDays } from '@core/bookings/pricing';
import { toDayjs, toIsoDate } from '@core/format/dates';
import type { Session } from '@core/session/types';

import { DesignCard } from './DesignCard';
import { DesignModal } from './DesignModal';

const pageSize = 60;

/**
 * The catalogue over a pair of dates — every design stocked here as a photograph-first card, and a
 * design's page over it with its looks, sizes, prices and what would be set aside.
 *
 * <p>
 * <strong>One call for the whole page</strong> (`GET /api/availability/catalogue`), where the
 * booking rail still makes one per design. It answers the counter's commonest question — "have you
 * got the maroon in a 40 for the 20th?" — without starting a booking, and its card and design page
 * are the ones the new booking flow is built from next.
 * </p>
 *
 * <p>
 * The garment-by-garment work — calendars, blocks, chasing a hold — stays on Availability.
 * </p>
 */
export function CataloguePage({ session }: { session: Session }) {
  const today = dayjs().format('YYYY-MM-DD');

  const [fromDate, setFromDate] = useState<string | undefined>(today);
  const [toDate, setToDate] = useState<string | undefined>(
    dayjs().add(2, 'day').format('YYYY-MM-DD'),
  );
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState<number | undefined>();
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<number | null>(null);

  const debouncedSearch = useDebounced(search);

  // Any narrowing starts again at the first page: page 3 of everything is an empty page of
  // sherwanis, which reads as "none in stock".
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, categoryId, fromDate, toDate]);

  const ordered = !!fromDate && !!toDate && fromDate <= toDate;

  const catalogue = useCatalogue(session.branch?.id, {
    fromDate: ordered ? fromDate : undefined,
    toDate: ordered ? toDate : undefined,
    search: debouncedSearch,
    categoryId,
    page,
    pageSize,
  });

  const categories = useItemCategories(session.branch?.id);
  const chips = (categories.data ?? []).filter((category) => category.depth <= 1);

  const days = ordered ? chargeableDays(fromDate, toDate) : 0;
  const designs = catalogue.data?.designs ?? [];
  const open = designs.find((design) => design.itemId === openId) ?? null;
  // Stable, so the popup's Esc listener is not re-registered on every refetch.
  const closeDesign = useCallback(() => setOpenId(null), []);

  return (
    <>
      <PageHeading
        title="Showroom"
        subtitle="Every design in stock here, as it stands on the dates you choose. Open one for its sizes, colours and prices."
      />

      <Card className="mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex w-40 flex-col gap-1 max-sm:w-auto max-sm:min-w-0 max-sm:flex-1">
            <span className="text-[length:var(--scale-field-hint)] font-semibold text-gray-900">
              From
            </span>
            <DatePicker
              value={toDayjs(fromDate) ?? null}
              onChange={(value) => setFromDate(toIsoDate(value))}
              allowClear={false}
              style={{ width: '100%' }}
            />
          </label>
          <label className="flex w-40 flex-col gap-1 max-sm:w-auto max-sm:min-w-0 max-sm:flex-1">
            <span className="text-[length:var(--scale-field-hint)] font-semibold text-gray-900">
              To
            </span>
            <DatePicker
              value={toDayjs(toDate) ?? null}
              onChange={(value) => setToDate(toIsoDate(value))}
              allowClear={false}
              style={{ width: '100%' }}
            />
          </label>
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search a design, its code or brand"
            allowClear
            prefix={<i className="icon icon-search text-default" />}
            className="max-w-80 flex-1 max-sm:max-w-none max-sm:flex-[1_0_100%]"
          />
        </div>

        {chips.length > 0 && (
          // One row that scrolls on its own on a phone: wrapped, eleven categories took six rows
          // and pushed every design below the first screen.
          <div className="mt-3 flex flex-wrap gap-1.5 max-sm:-mx-1 max-sm:flex-nowrap max-sm:overflow-x-auto max-sm:px-1 max-sm:pb-1">
            <Chip on={categoryId === undefined} onClick={() => setCategoryId(undefined)}>
              All
            </Chip>
            {chips.map((category) => (
              <Chip
                key={category.id}
                on={categoryId === category.id}
                onClick={() => setCategoryId(category.id)}
              >
                {category.name}
              </Chip>
            ))}
          </div>
        )}

        {!ordered && (
          <p className="text-warning mt-3 mb-0 text-[length:var(--scale-field-hint)]">
            The return date comes before the pick-up date.
          </p>
        )}
      </Card>

      {catalogue.isError && <ApiErrorAlert error={catalogue.error} />}

      {open && (
        <DesignModal
          key={open.itemId}
          design={open}
          days={days}
          currency="₹"
          onClose={closeDesign}
        />
      )}

      <div>
        {catalogue.isLoading ? (
          <p className="text-default py-10 text-center">Reading the catalogue…</p>
        ) : designs.length === 0 ? (
          <Card>
            <EmptyState
              icon="icon icon-shirt"
              title={
                debouncedSearch || categoryId !== undefined
                  ? 'No design in stock matches that'
                  : `Nothing in stock at ${session.branch?.name ?? 'this branch'}`
              }
              hint="Only designs with a garment on this branch’s rail are shown."
            />
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xxl:grid-cols-5">
              {designs.map((design) => (
                <DesignCard
                  key={design.itemId}
                  design={design}
                  days={days}
                  currency="₹"
                  selected={design.itemId === openId}
                  onOpen={() => setOpenId(design.itemId)}
                />
              ))}
            </div>

            {(catalogue.data?.total ?? 0) > pageSize && (
              <div className="mt-4 flex justify-end">
                <Pagination
                  current={page}
                  total={catalogue.data?.total ?? 0}
                  pageSize={pageSize}
                  onChange={setPage}
                  showSizeChanger={false}
                  showTotal={(count) => `${count} designs`}
                />
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

function Chip({
  on,
  onClick,
  children,
}: {
  on: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={`shrink-0 cursor-pointer rounded-full border px-3 whitespace-nowrap py-1 text-[length:var(--scale-btn-sm)] font-semibold ${
        on ? 'bg-primary border-primary text-white' : 'border-border-color text-default bg-white'
      }`}
    >
      {children}
    </button>
  );
}
